import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { getAmount, getDateWithDay } from "../../utils/Utils";
import { Pagination } from "../table/Pagination";
import {
    canMarkInvoiceAsPaidOperator,
    canMarkInvoiceAsPaidProducer,
    getInvoiceValueFromString,
    getTooltipValue, isInvoicePaidProducer, isInvoicePayable
} from "./InvoiceHelper";
import { Invoice } from "../interfaces/Invoice";
import { VtmnTooltip, VtmnLoader } from "@vtmn/react";
import { Role } from "../interfaces/Role";

type InvoiceTableProps = {
    invoices: Invoice[],
    role: Role,
    headers: string[],
    details: string[],
    clickables: any,
    changePage: (page: number) => any,
    updateAsPaidProducer: (invoice : Invoice, callback: () => void) => any
    updateAsPaidOperator: (invoice : Invoice, callback: () => void) => any
}

export const InvoiceTable = ({ invoices, role, headers, details, clickables, changePage, updateAsPaidProducer, updateAsPaidOperator }: InvoiceTableProps) => {
    let [rowSelected, setRowSelected] = useState(-1);
    const navigate = useNavigate();
    const load = useSelector((state: any) => state.loader.value)

    const [spinButtonTab, setSpinButtonTab] = useState<Record<string, boolean>>({});
    const [callRunningTab, setCallRunningTab] = useState<Record<string, boolean>>({});


    const markAsPaidbutton = (invoice: Invoice) => {
        const startSpinning = () => {
            setSpinButtonTab(prevTab => ({
                ...prevTab,
                [invoice.id]: true
            }));
            setCallRunningTab(prevTab => ({
                ...prevTab,
                [invoice.id]: true
            }));
            setTimeout(() => {
                setSpinButtonTab(prevTab => ({
                    ...prevTab,
                    [invoice.id]: false
                }));
            }, 2000);
        };
        const notifyEndCall = () => {
            setCallRunningTab(prevTab => ({
                ...prevTab,
                [invoice.id]: false
            }));
        }

        if(spinButtonTab[invoice.id] || callRunningTab[invoice.id]) {
            return (
                <span>
                    <button
                        className="vtmn-btn vtmn-btn_variant--secondary">
                        Waiting <span style={{marginLeft: '1em'}}><VtmnLoader size="small"/> </span>
                    </button>
                </span>
            );
        }
        const isProducer = canMarkInvoiceAsPaidProducer(role, invoice);
        const isOperator = canMarkInvoiceAsPaidOperator(role, invoice);
        const label = isInvoicePayable(invoice.payout_status) ? 'Paid Producer' : isInvoicePaidProducer(invoice) ? 'Paid Operator' : 'Already Paid';
        const update = isProducer ? updateAsPaidProducer : isOperator ? updateAsPaidOperator : () => {};
        return (
            <span>
                <button
                    className="vtmn-btn vtmn-btn_variant--secondary"
                    disabled={!isProducer && !isOperator}
                    onClick={() => {
                        startSpinning();
                        update(invoice, notifyEndCall);
                    }}
                >
                    {label}
                </button>
            </span>
        );
    }

    const tableElement = (element : string, invoice : Invoice) => {
        const value = getInvoiceValueFromString(invoice, element);
        let content;
        if(element.indexOf('amount') !== -1){
            content = getAmount(value, invoice["currency"])
        }else if(element.indexOf('date') !== -1){
            content = getDateWithDay(value)
        }else if (element ==='mark_as_paid'){
            content =  <VtmnTooltip tooltip={getTooltipValue(role, invoice)}>{markAsPaidbutton(invoice)}</VtmnTooltip> 
        }else{
            content = typeof value === 'object' 
                ? value.map((v: string, index: number) => index === value.length - 1 ? v : `${v} / `) 
                : value
        }
        return content;
    }

    return (
    <>{!load && 
    <div>
        <div className="block vtmn-flex vtmn-flex-row vtmn-flex-wrap vtmn-justify-center vtmn-mb-4">
            <p className="vtmn-typo_title-5">Total : {invoices?.length}</p>
        </div>
        <table className="vtmn-table-fixed">
            <thead>
                <tr>
                    {headers.map((header: string, i: Number) => {
                        return (
                        <>  
                            {i === 0 && <td></td>}
                            <th key={header}>
                               <>{header.replaceAll('_', ' ')}</>
                            </th>
                        </>
                        )
                    })}
                </tr>
            </thead>
            <tbody>
                {invoices.map((invoice: Invoice, index: number) => {

                    return (
                        <>
                            <tr className="header"  key={index}>
                                {headers.map((header: string, i: number) => {
                                    return (
                                            <>
                                                {i === 0 && details &&
                                                    <td>
                                                        <span 
                                                            className="vtmn-text-active-brand vtmx-eye-fill vtmn-cursor-pointer" aria-hidden="true" 
                                                            onClick={() => details ? setRowSelected(rowSelected === index ? -1 : index) : null}/>
                                                    </td>
                                                }
                                                <td key={`${header}-${index}`} 
                                                    className={clickables && clickables[header] && getInvoiceValueFromString(invoice, header) ? 'vtmn-link' : header.indexOf('amount') !== -1 ? 'vtmn-text-right' : ''} 
                                                    onClick={() => clickables && clickables[header] && getInvoiceValueFromString(invoice, header) ? navigate(clickables[header] + getInvoiceValueFromString(invoice, header)) : null}>
                                                    {tableElement(header, invoice)}
                                                </td>
                                            </>
                                            )
                                })}
                            </tr>
                            {rowSelected === index && <tr className="detail" key={`${index}detail`}>
                                <td colSpan={headers.length + 1}>
                                    <div className="vtmn-grid vtmn-grid-cols-3 vtmn-gap-0">
                                        {details.map((detail: string) => {
                                            return <>
                                                {getInvoiceValueFromString(invoice, detail) && 
                                                    <p className="block">
                                                        <span className="vtmn-capitalize">{`${detail.replaceAll('_', ' ')} : `}  </span>
                                                        {tableElement(detail, invoice)}
                                                    </p>
                                                }
                                            </>
                                        })}
                                    </div>
                                </td>
                            </tr>}
                        </>
                    )
                })}
            </tbody>
        </table>
        <Pagination result={invoices} changePage={changePage} />
    </div>}
    </>)
}

